import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from '@/app/layouts/RootLayout';
import { MarketingLayout } from '@/app/layouts/MarketingLayout';
import { AppLayout } from '@/app/layouts/AppLayout';
import { CommunityLayout } from '@/app/layouts/CommunityLayout';
import { NotFoundPage } from '@/app/NotFoundPage';
import { LandingPage } from '@/features/marketing/LandingPage';
import { AboutPage } from '@/features/marketing/AboutPage';
import { PricingPage } from '@/features/marketing/PricingPage';
import { CommunityPage } from '@/features/communities/CommunityPage';
import { CommunityHubLayout } from '@/app/layouts/CommunityHubLayout';
import { MyCommunitiesPage } from '@/features/communities/my-communities/MyCommunitiesPage';
import { NotificationsPage } from '@/features/profile/NotificationsPage';
import { MarketplacePage } from '@/features/marketplace/MarketplacePage';
import { ThemeStudioPage } from '@/features/studio/ThemeStudioPage';
import { ProfileStudioPage } from '@/features/studio/ProfileStudioPage';
import { SignInPage } from '@/features/auth/SignInPage';
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ProfileHandlePage } from '@/features/profile/ProfileHandlePage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { InboxPage } from '@/features/dm/InboxPage';
import { PinnedMessagesPage } from '@/features/dm/PinnedMessagesPage';
import { DirectMessagePage } from '@/features/dm/DirectMessagePage';

/**
 * RootLayout sits above everything so the OAuth callback catcher runs on every
 * route. Inside it: public marketing surfaces and the in-app shell. Auth-gated
 * routes wrap their element in <RequireAuth> (see app/router/RequireAuth).
 */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <MarketingLayout />,
        children: [
          { path: '/', element: <LandingPage /> },
          { path: '/about', element: <AboutPage /> },
          { path: '/pricing', element: <PricingPage /> },
          { path: '/sign-in', element: <SignInPage /> },
        ],
      },
      {
        element: <CommunityLayout />,
        children: [{ path: '/c/:communityId', element: <CommunityPage /> }],
      },
      {
        element: <CommunityHubLayout />,
        children: [{ path: '/communities', element: <MyCommunitiesPage /> }],
      },
      {
        element: <AppLayout />,
        children: [
          { path: '/me', element: <ProfilePage /> },
          { path: '/u/:handle', element: <ProfileHandlePage /> },
          { path: '/marketplace', element: <MarketplacePage /> },
          { path: '/notifications', element: <NotificationsPage /> },
          { path: '/inbox', element: <InboxPage /> },
          { path: '/inbox/pins', element: <PinnedMessagesPage /> },
          { path: '/dm/:conversationId', element: <DirectMessagePage /> },
          { path: '/settings', element: <SettingsPage /> },
        ],
      },
      // Full-screen WINR Studio (Figma-style editors) — no app chrome, its own shell.
      // `/studio/:communityId` edits a real owned community; `/studio` is a blank theme;
      // `/studio/profile` is the Profile Studio (static segment outranks the param).
      { path: '/studio', element: <ThemeStudioPage /> },
      { path: '/studio/profile', element: <ProfileStudioPage /> },
      { path: '/studio/:communityId', element: <ThemeStudioPage /> },
      // OAuth / magic-link / recovery landing — exchanges ?code= for a session.
      { path: '/auth/callback', element: <AuthCallbackPage /> },
      // Explore is now the home page (OG one-page model).
      { path: '/explore', element: <Navigate to="/" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
